import { useState } from 'react';
import dayjs from 'dayjs';
import { FolderOpen, Library, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
  RadioGroup,
  RadioGroupItem,
  Spinner,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useCreateSchedule,
  useUpdateSchedule,
  fetchScheduleDuration,
  type Asset,
  type Channel,
  type Schedule,
  type CreateScheduleInput,
} from '@/api/fastchannel';
import { trackFeatureAction } from '@/lib/analytics-events';
import { formatDurationClock } from '../shared';
import { resolvePlayoutMode, isPlaylistMode } from '../playout-modes';
import { FileManagerDialog } from '../FileManagerDialog';
import { LibraryPickerDialog } from '../LibraryPickerDialog';
import { DateTimePicker } from '@/components/date-time-picker/DateTimePicker';

interface ScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUuid: string;
  channel: Channel;
  mode: 'create' | 'edit';
  schedule?: Schedule;
  /** End time (ms) of the last program — used to default the next start. */
  lastEnd?: number;
}

const TYPES = [
  { value: 'live', label: 'Live event' },
  { value: 'vod', label: 'Video on demand' },
  { value: 'ads', label: 'Advertisement' },
];

export function ScheduleDialog({
  open,
  onOpenChange,
  serviceUuid,
  channel,
  mode,
  schedule,
  lastEnd,
}: ScheduleDialogProps) {
  const { show } = useToast();
  const isEdit = mode === 'edit';
  // Playlist modes (loop/once/shuffle) play in order, not by absolute start time,
  // so the start-time field is hidden — playout order is set by drag-reorder.
  const playlist = isPlaylistMode(resolvePlayoutMode(channel));

  const [type, setType] = useState<string>(schedule?.type ?? 'vod');
  const [title, setTitle] = useState(schedule?.title ?? '');
  const [description, setDescription] = useState(schedule?.description ?? '');
  const [url, setUrl] = useState(schedule?.url ?? '');
  const [duration, setDuration] = useState<number>(schedule?.duration ?? 0);
  const [resolving, setResolving] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Full start datetime (epoch ms): edit → the saved start; create → right after
  // the last program, else now. The day filter no longer scopes the start.
  const defaultStart = () => {
    if (schedule?.start_time) return schedule.start_time;
    if (lastEnd) return lastEnd;
    return Date.now();
  };
  const [start, setStart] = useState<number>(defaultStart);

  const create = useCreateSchedule(serviceUuid, channel.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Schedule added' });
      trackFeatureAction('fastchannel', 'schedule', 'create');
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not add schedule' }),
  });
  const update = useUpdateSchedule(serviceUuid, channel.id, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Schedule updated' });
      trackFeatureAction('fastchannel', 'schedule', 'edit');
      onOpenChange(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not update schedule' }),
  });

  const handleSelectFile = async (sourceUrl: string) => {
    setUrl(sourceUrl);
    setDuration(0);
    setResolving(true);
    try {
      const d = await fetchScheduleDuration(sourceUrl);
      setDuration(d ?? 0);
      if (!d) show({ tone: 'warning', title: 'Could not read media duration' });
    } catch {
      show({ tone: 'danger', title: 'Could not read media duration' });
    } finally {
      setResolving(false);
    }
  };

  /** Library assets carry a resolved duration, so set url + duration directly and
   *  skip the ffprobe duration probe the storage-file path needs. */
  const handleSelectAsset = (asset: Asset) => {
    // The library picker only surfaces ready assets, but guard anyway: a
    // not-yet-normalized asset has an empty url and would make an unplayable slot.
    if (!asset.url) {
      show({ tone: 'warning', title: 'Asset is still processing — not ready to schedule' });
      return;
    }
    setUrl(asset.url);
    setDuration(asset.duration_seconds);
    if (!asset.duration_seconds) {
      show({ tone: 'warning', title: 'Asset has no known duration' });
    }
  };

  const startD = dayjs(start).millisecond(0);
  const endD = startD.add(duration, 'second');

  const valid = title.trim().length > 0 && !!url && duration > 0 && !resolving;
  const busy = create.isPending || update.isPending;

  const submit = () => {
    const payload: CreateScheduleInput = {
      channel: channel.name,
      title: title.trim(),
      description: description.trim() || undefined,
      type,
      url,
      start_time: startD.valueOf(),
      end_time: endD.valueOf(),
      duration,
      live_url: type === 'live' ? url : undefined,
    };
    if (isEdit && schedule) {
      update.mutate({ scheduleId: schedule.id, schedule: payload });
    } else {
      create.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit program' : 'Add program'}</DialogTitle>
          <DialogDescription>
            {playlist
              ? 'Programs play in playlist order. Reorder them from the schedule list.'
              : 'Programs are timed automatically from their start and the media duration.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <Field label="Program type">
              {() => (
                <RadioGroup
                  value={type}
                  onValueChange={setType}
                  style={{ display: 'flex', gap: 'var(--vn-space-4)', flexWrap: 'wrap' }}
                >
                  {TYPES.map((t) => (
                    <label
                      key={t.value}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--vn-font-size-md)' }}
                    >
                      <RadioGroupItem value={t.value} /> {t.label}
                    </label>
                  ))}
                </RadioGroup>
              )}
            </Field>

            <Field label="Title" required>
              {(props) => (
                <Input
                  {...props}
                  value={title}
                  placeholder="Program title"
                  onChange={(e) => setTitle(e.target.value)}
                />
              )}
            </Field>

            <Field label="Description">
              {(props) => (
                <Textarea
                  {...props}
                  value={description}
                  rows={2}
                  placeholder="Optional program description"
                  onChange={(e) => setDescription(e.target.value)}
                />
              )}
            </Field>

            <Field
              label="Source"
              required
              description="Pick a media file from your storage. Its duration is read automatically."
            >
              {(props) => (
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                  <Input
                    {...props}
                    value={url}
                    readOnly
                    placeholder="No file selected"
                    endSlot={
                      resolving ? (
                        <Spinner size="sm" />
                      ) : url && duration > 0 ? (
                        <Check size={14} color="var(--vn-success-text)" />
                      ) : undefined
                    }
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <Button
                    variant="secondary"
                    startIcon={<Library size={15} />}
                    onClick={() => setLibraryOpen(true)}
                  >
                    Library
                  </Button>
                  <Button
                    variant="secondary"
                    startIcon={<FolderOpen size={15} />}
                    onClick={() => setFileOpen(true)}
                  >
                    Storage
                  </Button>
                </div>
              )}
            </Field>

            {playlist ? (
              <Field label="Duration" description="Programs play back-to-back in playlist order.">
                {(props) => (
                  <Input {...props} value={formatDurationClock(duration)} readOnly disabled style={{ minWidth: 0 }} />
                )}
              </Field>
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
                <Field label="Start">
                  {() => <DateTimePicker value={start} onChange={setStart} />}
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--vn-space-3)' }}>
                  <Field label="Duration">
                    {(props) => (
                      <Input {...props} value={formatDurationClock(duration)} readOnly disabled style={{ minWidth: 0 }} />
                    )}
                  </Field>
                  <Field label="End">
                    {(props) => (
                      <Input {...props} value={endD.format('MMM D, HH:mm:ss')} readOnly disabled style={{ minWidth: 0 }} />
                    )}
                  </Field>
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!valid || busy} onClick={submit}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add program'}
          </Button>
        </DialogFooter>
      </DialogContent>

      <FileManagerDialog
        open={fileOpen}
        onOpenChange={setFileOpen}
        serviceUuid={serviceUuid}
        onSelect={(src) => {
          setFileOpen(false);
          void handleSelectFile(src);
        }}
      />

      <LibraryPickerDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        serviceUuid={serviceUuid}
        onSelect={(asset) => {
          setLibraryOpen(false);
          handleSelectAsset(asset);
        }}
      />
    </Dialog>
  );
}
